import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

const DEFAULTS = {
  merge_max_output_tokens: 6144,
  merge_analysis_max_output_tokens: 2048,
  merge_thinking_budget: 0,
  merge_temperature: 0.1,
  merge_target_min_ratio: 0.9,
  merge_target_max_ratio: 1.15,
  merge_disable_afc: true,
};

const asBool = (value, fallback) => {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return ['true', '1', 'yes'].includes(value.toLowerCase());
  return Boolean(value);
};

const asNumber = (value, fallback) => {
  if (value === undefined || value === null || value === '') return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const asInt = (value, fallback) => {
  if (value === undefined || value === null || value === '') return fallback;
  const parsed = parseInt(String(value), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const buildState = (settings) => ({
  merge_max_output_tokens: asInt(settings.merge_max_output_tokens, DEFAULTS.merge_max_output_tokens),
  merge_analysis_max_output_tokens: asInt(settings.merge_analysis_max_output_tokens, DEFAULTS.merge_analysis_max_output_tokens),
  merge_thinking_budget: asInt(settings.merge_thinking_budget, DEFAULTS.merge_thinking_budget),
  merge_temperature: asNumber(settings.merge_temperature, DEFAULTS.merge_temperature),
  merge_target_min_ratio: asNumber(settings.merge_target_min_ratio, DEFAULTS.merge_target_min_ratio),
  merge_target_max_ratio: asNumber(settings.merge_target_max_ratio, DEFAULTS.merge_target_max_ratio),
  merge_disable_afc: asBool(settings.merge_disable_afc, DEFAULTS.merge_disable_afc),
});

const LLMTuningSettings = ({ settings, onSave, saving = false }) => {
  const [form, setForm] = useState(() => buildState(settings || {}));

  useEffect(() => {
    setForm(buildState(settings || {}));
  }, [settings]);

  const handleNumberChange = (key, value, parser = Number) => {
    if (value === '' || value === '-') {
      setForm((prev) => ({ ...prev, [key]: value }));
      return;
    }
    const parsed = parser(value);
    if (!Number.isFinite(parsed)) return;
    setForm((prev) => ({ ...prev, [key]: parsed }));
  };

  const handleResetDefaults = () => {
    setForm(DEFAULTS);
  };

  const handleSave = async () => {
    const payload = {
      merge_max_output_tokens: asInt(form.merge_max_output_tokens, DEFAULTS.merge_max_output_tokens),
      merge_analysis_max_output_tokens: asInt(form.merge_analysis_max_output_tokens, DEFAULTS.merge_analysis_max_output_tokens),
      merge_thinking_budget: asInt(form.merge_thinking_budget, DEFAULTS.merge_thinking_budget),
      merge_temperature: asNumber(form.merge_temperature, DEFAULTS.merge_temperature),
      merge_target_min_ratio: asNumber(form.merge_target_min_ratio, DEFAULTS.merge_target_min_ratio),
      merge_target_max_ratio: asNumber(form.merge_target_max_ratio, DEFAULTS.merge_target_max_ratio),
      merge_disable_afc: asBool(form.merge_disable_afc, DEFAULTS.merge_disable_afc),
    };

    if (payload.merge_target_min_ratio <= 0 || payload.merge_target_max_ratio <= 0) {
      toast.error('Length ratios must be greater than 0');
      return;
    }

    if (payload.merge_target_max_ratio < payload.merge_target_min_ratio) {
      toast.error('Maximum target ratio must be greater than or equal to minimum target ratio');
      return;
    }

    if (payload.merge_max_output_tokens < 256 || payload.merge_analysis_max_output_tokens < 256) {
      toast.error('Max output token values must be at least 256');
      return;
    }

    if (payload.merge_thinking_budget < 0) {
      toast.error('Thinking budget cannot be negative');
      return;
    }

    await onSave(payload);
  };

  return (
    <section className="admin-llm-tuning">
      <div className="row-actions">
        <h3>Merge Tuning</h3>
        <button
          className="btn btn--sm"
          type="button"
          onClick={handleResetDefaults}
          disabled={saving}
        >
          Reset to Defaults
        </button>
      </div>
      <p className="muted">
        Tune merge generation behavior at runtime. These values are saved in system settings and apply to new requests without restarting.
      </p>

      <div className="admin-tuning-grid">
        <div className="form-group">
          <label htmlFor="merge-max-output-tokens">Merge max output tokens</label>
          <input
            id="merge-max-output-tokens"
            className="form-control"
            type="number"
            min="256"
            step="1"
            value={form.merge_max_output_tokens}
            onChange={(e) => handleNumberChange('merge_max_output_tokens', e.target.value, (v) => parseInt(v, 10))}
          />
        </div>

        <div className="form-group">
          <label htmlFor="merge-analysis-max-output-tokens">Analysis max output tokens</label>
          <input
            id="merge-analysis-max-output-tokens"
            className="form-control"
            type="number"
            min="256"
            step="1"
            value={form.merge_analysis_max_output_tokens}
            onChange={(e) => handleNumberChange('merge_analysis_max_output_tokens', e.target.value, (v) => parseInt(v, 10))}
          />
        </div>

        <div className="form-group">
          <label htmlFor="merge-thinking-budget">Thinking budget</label>
          <input
            id="merge-thinking-budget"
            className="form-control"
            type="number"
            min="0"
            step="1"
            value={form.merge_thinking_budget}
            onChange={(e) => handleNumberChange('merge_thinking_budget', e.target.value, (v) => parseInt(v, 10))}
          />
        </div>

        <div className="form-group">
          <label htmlFor="merge-temperature">Temperature</label>
          <input
            id="merge-temperature"
            className="form-control"
            type="number"
            min="0"
            max="2"
            step="0.05"
            value={form.merge_temperature}
            onChange={(e) => handleNumberChange('merge_temperature', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="merge-target-min-ratio">Target min ratio</label>
          <input
            id="merge-target-min-ratio"
            className="form-control"
            type="number"
            min="0.1"
            step="0.05"
            value={form.merge_target_min_ratio}
            onChange={(e) => handleNumberChange('merge_target_min_ratio', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="merge-target-max-ratio">Target max ratio</label>
          <input
            id="merge-target-max-ratio"
            className="form-control"
            type="number"
            min="0.1"
            step="0.05"
            value={form.merge_target_max_ratio}
            onChange={(e) => handleNumberChange('merge_target_max_ratio', e.target.value)}
          />
        </div>
      </div>

      <label className="checkbox-inline admin-tuning-checkbox">
        <input
          type="checkbox"
          checked={form.merge_disable_afc}
          onChange={(e) => setForm((prev) => ({ ...prev, merge_disable_afc: e.target.checked }))}
        />
        Disable AFC
      </label>

      <div className="form-actions">
        <button
          className="btn btn--primary"
          type="button"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Tuning'}
        </button>
      </div>
    </section>
  );
};

export default LLMTuningSettings;
